import { useNavigate } from 'react-router-dom';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import { DirectoryPage } from '../components/DirectoryPage';
import { lastActivityColumn, statusColumn } from '../components/columns';
import { NameCell } from '../components/NameCell';
import { ServiceChips } from '../components/ServiceChips';
import { StatusChip } from '../components/StatusChip';
import { AddCarrierDialog } from '../features/carriers/AddCarrierDialog';
import { CARRIER_MODES, type Carrier } from '../features/carriers/sample';
import { RISK_TONE, useCarrierStats } from '../features/carriers/stats';
import { useCarriers } from '../features/carriers/useCarriers';
import { shortDate } from '../lib/dates';
import { SERVICES, type Service } from '../lib/services';

/** Every shipping line and airline, with how our shipments with them went. A click opens the carrier. */
export function CarriersPage() {
  const navigate = useNavigate();
  const carriers = useCarriers();
  const statsOf = useCarrierStats();
  const all = carriers.items;
  return (
    <DirectoryPage<Carrier>
      title="Carriers"
      subtitle="Manage shipping lines, airlines, and transport companies. Compare performance and view service history."
      addLabel="Add Carrier"
      noun="carriers"
      items={all}
      tiles={{
        total: {
          title: 'Total Carriers',
          note: 'All registered carriers',
          icon: <DirectionsBoatOutlined />,
          tone: 'info',
        },
        active: {
          title: 'Active Carriers',
          note: 'Used in recent shipments',
          icon: <TaskAltOutlined />,
          tone: 'success',
        },
        fresh: { title: 'New Carriers', note: 'Added in the last 30 days', icon: <GroupAddOutlined />, tone: 'info' },
        attention: {
          title: 'Needs Attention',
          note: 'Service issues or low performance',
          icon: <ErrorOutlineOutlined />,
          tone: 'error',
        },
      }}
      needsAttention={(c) => statsOf(c).needsAttention}
      searchText={(c) => [c.name, c.country, c.coverage, c.contact, c.email].join(' ')}
      searchPlaceholder="Search carriers by name, country, service, route…"
      filters={[
        { label: 'Mode', options: CARRIER_MODES, matches: (c, v) => c.mode === v },
        { label: 'Service', options: SERVICES, matches: (c, v) => c.services.includes(v as Service) },
        {
          label: 'Country',
          options: [...new Set(all.map((c) => c.country))].sort(),
          matches: (c, v) => c.country === v,
        },
      ]}
      rowKey={(c) => c.id}
      onOpen={(c) => void navigate(`/carriers/${c.id}`)}
      columns={[
        { header: 'Carrier Name', primary: true, render: (c) => <NameCell name={c.name} /> },
        { header: 'Mode', nowrap: true, render: (c) => c.mode },
        { header: 'Country', render: (c) => c.country },
        { header: 'Services', render: (c) => <ServiceChips services={c.services} /> },
        { header: 'Coverage / Main Routes', render: (c) => c.coverage },
        { header: 'Contact Person', render: (c) => c.contact },
        { header: 'Email', render: (c) => c.email },
        { header: 'Total Shipments', nowrap: true, render: (c) => statsOf(c).total },
        {
          header: 'Reliability',
          nowrap: true,
          render: (c) => (statsOf(c).reliability === null ? '–' : `${statsOf(c).reliability}%`),
        },
        {
          header: 'Rollover Risk',
          nowrap: true,
          render: (c) => <StatusChip label={statsOf(c).risk} tone={RISK_TONE[statsOf(c).risk]} />,
        },
        lastActivityColumn((c) => (statsOf(c).lastActivity ? shortDate(statsOf(c).lastActivity) : '')),
        statusColumn(),
      ]}
      addDialog={(d) => <AddCarrierDialog {...d} onAdd={carriers.add} />}
    />
  );
}
